import { Button } from "@/components/ui/button";
import { Calendar, ExternalLink, Shield, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { PricingPlansGrid } from "./PricingPlansGrid";
import { StripeProduct, StripePrice } from "@/services/payments";

interface PricingLayoutProps {
  products: StripeProduct[];
  prices: StripePrice[];
  onPlanAction: (productId: string, isAnnual: boolean) => void;
  loading?: string | null;
  className?: string;
  mode?: "paywall" | "billing";
}

export function PricingLayout({
  products,
  prices,
  onPlanAction,
  loading,
  className,
  mode = "paywall",
}: PricingLayoutProps) {
  return (
    <div className={cn("w-full max-w-5xl  p-4 relative", className)}>
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-3">
          {mode === "paywall"
            ? "Start Trial to generate your first article"
            : "Time to upgrade!"}
        </h1>
        {mode === "paywall" ? (
          <div className="flex items-center justify-center gap-2 text-gray-600 mb-2">
            <span className="text-2xl">🎉</span>
            <span className="font-medium">Free 3-day trial</span>
            <span>•</span>
            <span>No commitment</span>
            <span>•</span>
            <span>Cancel anytime</span>
          </div>
        ) : (
          <p className="text-gray-600">
            Select the perfect plan for your content creation needs
          </p>
        )}
      </div>

      {/* Plans Grid */}
      <div className="mb-8">
        <PricingPlansGrid
          products={products}
          prices={prices}
          mode={mode}
          onPlanAction={onPlanAction}
          loading={loading}
        />
      </div>

      {/* Trust Indicators - Only show for paywall mode */}
      {mode === "paywall" && (
        <>
          <div className="flex items-center justify-center gap-8 text-sm text-gray-500 mb-6">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span>Secure payment</span>
            </div>
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              <span>Cancel anytime</span>
            </div>
          </div>

          <div className="flex items-center justify-center gap-6 text-xs text-gray-400">
            <span>💳 No hidden fees</span>
            <span>🔒 Your data is secure</span>
            <span>⚡ Instant article generation</span>
          </div>
        </>
      )}

      {/* Alternative Options */}
      <div className="mt-8 pt-6 border-t border-gray-200 text-center">
        <p className="text-gray-500 text-sm mb-4">
          Not ready to subscribe? Try our platform first
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              window.open("https://cal.com/aicreator/30min", "_blank")
            }
          >
            <Calendar className="w-4 h-4 mr-2" />
            Book a Demo
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              window.open(
                "https://creator.li/free-seo-article-generator",
                "_blank",
              )
            }
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Try Free Article Generator
          </Button>
        </div>
      </div>
    </div>
  );
}
